import "../../css/App.css";
import {
  useStripe,
  useElements,
  PaymentElement,
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import {
  PaymentRequest,
  Stripe,
  StripeElements,
  StripePaymentRequestButtonElementClickEvent,
} from "@stripe/stripe-js";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Event, ThemeType } from "@markit/common.types";
import { MixpanelContext } from "../../context/AnalyticsService";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import { Colors } from "../../utils/colors";
import { paymentTimeoutHandler } from "../../utils/FullEventUtils/inputFormUtils";
import { isEventTicketsPaid } from "@markit/common.utils";
import { useTheme } from "../../hooks/useTheme";
import RectangleButton from "../../components/Buttons/RectangleButton";

type CheckoutFormProps = {
  customerPhone: string;
  totalAmount: number;
  customTicketIds: string[];
  clientSecret: string | undefined;
  isReadyForPayment: boolean;
  processing: boolean;
  setProcessing: (processing: boolean) => void;
  event: Event;
  onPress: (stripe: Stripe, elements: StripeElements) => Promise<void>;
  showPurchaseError: () => Promise<boolean>;
  rsvpToEvent: () => Promise<void>;
  ticketQuantity: number;
  disabled: boolean;
};

const CheckoutForm = ({
  customerPhone,
  totalAmount,
  customTicketIds,
  clientSecret,
  isReadyForPayment,
  processing,
  setProcessing,
  event,
  onPress,
  showPurchaseError,
  rsvpToEvent,
  ticketQuantity,
  disabled,
}: CheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { account } = useSelector(getAccountState);
  const { isRCA, accountData } = account;
  const { theme } = useTheme();

  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest>();
  const [useCredit, setUseCreditCard] = useState(false);
  const mixpanel = useContext(MixpanelContext);
  const paymentRequestInitIdRef = useRef(0);

  // Store the latest values in a ref to ensure the paymentRequest is always updated with the latest amount.
  const latestValuesRef = useRef({
    totalAmount,
    clientSecret,
    ticketQuantity,
    customerPhone,
    customTicketIds,
    event,
    rsvpToEvent,
  });
  latestValuesRef.current = {
    totalAmount,
    clientSecret,
    ticketQuantity,
    customerPhone,
    customTicketIds,
    event,
    rsvpToEvent,
  };

  // Disable the payment button if the user is an email based account, or if the disabled prop is true
  // disabled prop can be true if the purchase exceeds capacity (willExceedCapacity), etc
  const disablePayment = useMemo(
    () => accountData.email !== "" || disabled,
    [accountData.email, disabled]
  );

  // For handling apple pay
  useEffect(() => {
    if (!stripe || !elements) {
      return;
    }

    const initId = ++paymentRequestInitIdRef.current;
    const { totalAmount: initialTotalAmount } = latestValuesRef.current;
    const pr = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: "Ticket to " + event.title,
        amount: Math.round(initialTotalAmount),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    // Check the availability of the Payment Request API.
    pr.canMakePayment()
      .then((result) => {
        // Ignore stale async results from prior PaymentRequest instances.
        if (result && initId === paymentRequestInitIdRef.current) {
          setPaymentRequest(pr);
        }
      })
      .catch(() => {
        alert("Error showing payment sheet.");
      });

    const onCancel = async () => {
      const {
        totalAmount: currentTotalAmount,
        customerPhone: currentCustomerPhone,
        customTicketIds: currentCustomTicketIds,
        event: currentEvent,
      } = latestValuesRef.current;
      if (mixpanel) {
        mixpanel.track("Webapp: User Cancelled Apple Pay Payment Sheet", {
          event_id: currentEvent.id,
          event_type: isEventTicketsPaid(currentEvent) ? "paid" : "free",
          customTicketIds: currentCustomTicketIds,
          customer_phone: currentCustomerPhone,
          apple_pay: true,
          amount: currentTotalAmount,
        });
      }
      setProcessing(false);
    };

    const onPaymentMethod = async (e: any) => {
      const {
        totalAmount: currentTotalAmount,
        clientSecret: currentClientSecret,
        ticketQuantity: currentTicketQuantity,
        customerPhone: currentCustomerPhone,
        customTicketIds: currentCustomTicketIds,
        event: currentEvent,
        rsvpToEvent: currentRsvpToEvent,
      } = latestValuesRef.current;

      if (mixpanel) {
        mixpanel.track("Webapp: Attempting Ticket Purchase", {
          event_id: currentEvent.id,
          event_type: isEventTicketsPaid(currentEvent) ? "paid" : "free",
          customTicketIds: currentCustomTicketIds,
          customer_phone: currentCustomerPhone,
          apple_pay: true,
          amount: currentTotalAmount,
        });
      }
      if (!currentClientSecret) {
        e.complete("fail");
        setProcessing(false);
        return;
      }

      // Set payment timeout of 30 seconds
      const paymentTimeout = 30000;
      const timeoutPromise = new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve(new Error("Payment processing timed out. Please try again."));
        }, paymentTimeout);
      });

      const paymentPromise = new Promise((resolve, reject) => {
        stripe
          .confirmCardPayment(
            currentClientSecret,
            {
              payment_method: e.paymentMethod.id,
            },
            { handleActions: false }
          )
          .then((result) => {
            const { error: stripeError } = result;
            resolve(stripeError); // Resolve the Promise with the error object
          })
          .catch((error) => {
            resolve(
              new Error(
                "API call to Stripe confirmCardPayment failed: " + error
              )
            );
          });
      });

      const result = await Promise.race([paymentPromise, timeoutPromise]);

      // result is either instanceof Error or StripeError if there is an error
      // otherwise, result is undefined if payment is successful
      if (
        result instanceof Error &&
        result.message === "Payment processing timed out. Please try again."
      ) {
        e.complete("fail");
        await paymentTimeoutHandler(
          currentEvent.id,
          currentCustomerPhone,
          "",
          currentTotalAmount,
          currentTicketQuantity,
          isEventTicketsPaid(currentEvent) ? "paid" : "free",
          currentCustomTicketIds,
          currentCustomerPhone,
          mixpanel
        );

        alert(
          "Your payment has timed out. Please try again. This page will be refreshed."
        );
        window.location.reload();
      } else if (result) {
        // Show error to your customer (e.g., insufficient funds)
        e.complete("fail");
        setProcessing(false);
        mixpanel.track("Webapp: Stripe Error", {
          event_id: currentEvent.id,
          error_message: result,
          customTicketIds: currentCustomTicketIds,
          customer_phone: currentCustomerPhone,
        });
      } else {
        e.complete("success");
        mixpanel.track("Webapp: Bought a Ticket", {
          event_id: currentEvent.id,
          event_type: isEventTicketsPaid(currentEvent) ? "paid" : "free",
          customTicketIds: currentCustomTicketIds,
          customer_phone: currentCustomerPhone,
          apple_pay: true,
          amount: currentTotalAmount,
        });
        await currentRsvpToEvent();
        setProcessing(false);
      }
    };

    pr.on("cancel", onCancel);
    pr.on("paymentmethod", onPaymentMethod);

    return () => {
      pr.off("cancel", onCancel);
      pr.off("paymentmethod", onPaymentMethod);
    };
  }, [event.title, stripe, elements, setProcessing, mixpanel]);

  if (!elements || !stripe) {
    return null;
  }

  return (
    <>
      {paymentRequest && !disablePayment && (
        <div aria-disabled={processing} className="applePayElement">
          <PaymentRequestButtonElement
            options={{
              paymentRequest,
              style: {
                paymentRequestButton: {
                  theme: event.theme === ThemeType.Dark ? "light" : "dark",
                },
              },
            }}
            onClick={async (
              clickEvent: StripePaymentRequestButtonElementClickEvent
            ) => {
              const purchaseError = await showPurchaseError();
              if (purchaseError) {
                clickEvent.preventDefault();
                setProcessing(false);
                return false;
              }

              if (!isReadyForPayment) {
                clickEvent.preventDefault();
                setProcessing(false);
                mixpanel?.track("Webapp: Ready For Payment Error", {
                  event_id: event.id,
                  customTicketIds: customTicketIds,
                  customer_phone: customerPhone,
                });
                return false;
              }

              // Ensure Apple Pay sheet always opens with the latest amount.
              paymentRequest.update({
                total: {
                  label: "Ticket to " + event.title,
                  amount: Math.round(totalAmount),
                },
              });

              setProcessing(true);
            }}
          />
        </div>
      )}
      {useCredit ? (
        <>
          <PaymentElement options={{ wallets: { applePay: "never" } }} />
          <div className="AlignedColumn" style={{ marginTop: "14px" }}>
            <RectangleButton
              buttonLabel={<span>{"Pay with Card"}</span>}
              onPress={() => onPress(stripe, elements)}
              theme={theme}
              altPaddingVert={12}
              disabled={!stripe || processing || disablePayment}
              loading={processing}
            />
            {processing ? (
              <div
                style={{
                  ...theme.PrimaryText,
                  fontSize: 12,
                  fontWeight: "400",
                  textAlign: "center",
                  width: "60%",
                  marginTop: 7,
                }}
              >
                Please do not close this window while your payment is
                processing.
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <p
          className="useCreditCard"
          onClick={async () => {
            console.log("credit card amount: " + totalAmount);
            const purchaseError = await showPurchaseError();
            if (purchaseError) {
              return false;
            }
            if (!isReadyForPayment) {
              mixpanel.track("Webapp: Not Ready For Payment Error", {
                event_id: event.id,
                customTicketIds: customTicketIds,
                customer_phone: customerPhone,
              });
            } else {
              setUseCreditCard(true);
            }
          }}
          style={{
            color: disablePayment
              ? Colors.GRAY1
              : isRCA
                ? "#FE296C"
                : Colors.BLUE5,
            fontWeight: "500",
            fontSize: 14,
            cursor: "pointer",
            pointerEvents: disablePayment ? "none" : "all",
          }}
        >
          Use Credit Card
        </p>
      )}
    </>
  );
};

export default CheckoutForm;
